import { Component, computed, inject, input } from '@angular/core';
import { PaymentMethodError } from './types';
import { BANK_ACCOUNT_UNUSABLE, CARD_DECLINED, DO_NOT_HONOR, DO_NOT_HONOR_MESSAGE, errorTitles, messageByCode } from './utils';
import { AIWYN_PAYMENT_METHOD_VALIDATION_ERROR_MESSAGE, PaymentPlatformService } from '../../platforms';
import { isAuthFailure, SESSION_EXPIRED_MESSAGE } from '../../utils';
import { Logger } from '@aiwyn/shared';

@Component({
  selector: 'app-new-payment-method-failed',
  templateUrl: './new-payment-method-failed.component.html',
  styleUrl: './new-payment-method-failed.component.scss',
  standalone: false,
})
export class NewPaymentMethodFailedComponent {
  private paymentPlatformService = inject(PaymentPlatformService);
  private logger = inject(Logger);

  public error = input.required<PaymentMethodError>();

  public hasTitleMessage = computed(() => !!this.hasTitle());
  public message = computed(() => this.getErrorMessage());
  public title = computed(() => this.getErrorTitle());

  private hasTitle(): boolean {
    const err = this.error();

    return err.code === BANK_ACCOUNT_UNUSABLE || err.decline_code === DO_NOT_HONOR;
  }

  private getErrorMessage(): string {
    const error = this.error();
    this.logger.warn(`NewPaymentMethodFailedComponent : getErrorMessage : Error code: ${error.code} : Error message: ${error.message}`);

    // PP-3820: a 401 is an expired/missing session, not a payment-method problem. Short-circuit before
    // the decline/validation paths so we never leak the raw "Http failure response for …: 401 OK".
    if (isAuthFailure(error)) {
      return SESSION_EXPIRED_MESSAGE;
    }

    if (error.message === AIWYN_PAYMENT_METHOD_VALIDATION_ERROR_MESSAGE) {
      return this.paymentPlatformService.getValidationErrorMessage(error.validationErrors);
    }

    if (error.code === CARD_DECLINED) {
      return this.processCardDeclinedErrors();
    }

    if (messageByCode[error.code]) {
      return messageByCode[error.code];
    }

    return error.message;
  }

  private processCardDeclinedErrors(): string {
    const error = this.error();

    if (error.decline_code === DO_NOT_HONOR) {
      return DO_NOT_HONOR_MESSAGE;
    }

    return error.message;
  }

  private getErrorTitle(): string {
    if (!this.hasTitle()) {
      return;
    }

    const error = this.error();

    const match = errorTitles.find(
      (({ property, value }) => error[property] === value)
    );

    return match?.title;
  }
}
